/**
 * 2WP Child Theme - Main JavaScript
 */

// Import main stylesheet so Vite emits dist/main.css.
// Block CSS/JS are built as separate entries and loaded conditionally by PHP.
import '../scss/main.scss';

/**
 * DOM Ready handler
 */
const ready = (fn) => {
  if (document.readyState !== 'loading') {
    fn();
  } else {
    document.addEventListener('DOMContentLoaded', fn);
  }
};

/**
 * Initialize theme functionality
 */
const initTheme = () => {
  // Mobile nav toggle
  const toggle = document.getElementById('nav-toggle');
  const header = document.querySelector('.site-header');
  if (toggle && header) {
    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      header.classList.toggle('site-header--open', !expanded);
      // Lock background scroll while the full-screen overlay is open
      document.documentElement.classList.toggle('is-nav-open', !expanded);
    });
  }

  initHeaderDropdowns(header);
  initSmoothScroll();

  // Mobile submenu (+) expand/collapse. The parent link still navigates;
  // only the adjacent "+" button toggles the children inline.
  if (header) {
    header.querySelectorAll('.site-header__subtoggle').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const li = btn.closest('.site-header__item--has-children');
        if (!li) return;
        const open = li.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(open));
      });
    });
  }
};

/**
 * Click-to-toggle dropdown for the language switcher only.
 * Primary-nav parent items are real links: clicking navigates, and their
 * dropdown opens on hover/focus (CSS) — no JS click handling for them.
 * Closes on Escape and outside click. Dependency-free.
 */
const initHeaderDropdowns = (header) => {
  if (!header) return;

  // Only the language switcher is a click-toggle dropdown.
  const dropdowns = header.querySelectorAll('.lang-switcher');
  if (!dropdowns.length) return;

  const triggerSelector = '.lang-switcher__current';

  const closeAll = (except) => {
    dropdowns.forEach((dd) => {
      if (dd === except) return;
      dd.classList.remove('is-open');
      const t = dd.querySelector(triggerSelector);
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  };

  dropdowns.forEach((dd) => {
    const trigger = dd.querySelector(triggerSelector);
    if (!trigger) return;

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const isOpen = dd.classList.contains('is-open');
      closeAll(dd);
      dd.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', String(!isOpen));
    });
  });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAll();
  });

  // Close on outside click
  document.addEventListener('click', (e) => {
    if (!header.contains(e.target)) {
      closeAll();
      return;
    }
    // Click inside header but outside the switcher closes it too
    const inDropdown = e.target.closest('.lang-switcher');
    if (!inDropdown) closeAll();
  });
};

/**
 * Close the mobile nav overlay (if open) and restore background scroll. Shared
 * by the smooth-scroll handler so in-menu anchor links collapse the overlay
 * before scrolling to their target.
 */
const closeMobileNav = () => {
  const header = document.querySelector('.site-header');
  if (header) header.classList.remove('site-header--open');
  document.documentElement.classList.remove('is-nav-open');
  const toggle = document.getElementById('nav-toggle');
  if (toggle) toggle.setAttribute('aria-expanded', 'false');
};

/**
 * Smooth-scroll same-page hash links to their target. Delegated on the document
 * so it covers links anywhere (nav, content, blocks). Skips modifier-clicks,
 * new-tab/download links and cross-page links; honours prefers-reduced-motion
 * and keeps the URL hash + keyboard focus in sync.
 *
 * Bails on e.defaultPrevented, so interactive widgets that handle their own
 * hash links (and call preventDefault) keep working untouched.
 */
const initSmoothScroll = () => {
  // Stop this many pixels ABOVE the target so it doesn't sit flush at the very
  // top of the viewport (also leaves room for a sticky header if added later).
  const SCROLL_OFFSET = 100;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const scrollToY = (top) =>
    window.scrollTo({ top: Math.max(0, top), behavior: prefersReduced.matches ? 'auto' : 'smooth' });

  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }

    const link = e.target.closest('a[href]');
    if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) {
      return;
    }

    // Same document only (same origin + path + query). A hash is required.
    const url = new URL(link.href, window.location.href);
    if (
      url.origin !== window.location.origin ||
      url.pathname !== window.location.pathname ||
      url.search !== window.location.search
    ) {
      return;
    }

    if (!url.hash || url.hash === '#') {
      // A bare "#" link scrolls to the top.
      if (url.hash === '#') {
        e.preventDefault();
        closeMobileNav();
        scrollToY(0);
        history.pushState(null, '', url.pathname + url.search);
      }
      return;
    }

    const id = decodeURIComponent(url.hash.slice(1));
    const safeId = window.CSS && CSS.escape ? CSS.escape(id) : id;
    const target = document.getElementById(id) || document.querySelector('a[name="' + safeId + '"]');
    if (!target) return; // unknown target → leave default behaviour

    // Hidden target (offsetParent === null), e.g. an inactive tab panel managed
    // by a widget: don't intercept. Native navigation sets the hash and fires
    // `hashchange`, which the widget (e.g. the tabs block) listens for to reveal
    // and scroll to it. pushState wouldn't fire hashchange, and we can't scroll
    // to a display:none element anyway.
    if (target.offsetParent === null) return;

    e.preventDefault();
    closeMobileNav(); // collapse the mobile overlay first so layout is settled

    // getBoundingClientRect forces the reflow needed after closing the overlay;
    // subtract SCROLL_OFFSET so we stop above the target rather than flush to it.
    const top = target.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
    scrollToY(top);

    // Move focus for keyboard/AT users without a second jump.
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });

    history.pushState(null, '', url.hash);
  });
};

// Initialize on DOM ready
ready(initTheme);

// Export for potential module usage
export { ready, initTheme };
